import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { POCValue } from 'screens/student-details/styled-comps';
import { Row, FlexView, COLORS, toRGBA } from '@cuemath/comps';
import { Text, TextInput, Button as WCButton } from '@cuemath/web-comps';

import { postStudentTransferStatus } from 'actions/student-transfer/single-student';
import { getDateOfRequest } from 'utils/student-transfer';

import { LinkText } from '../slots-pending/styles';
import StudentTransferSlotsList from '../slots-pending/add-slots';
import { getSingleStudentTransferPostData } from '../../../../utils/student-transfer';

const Root = styled(FlexView)`
  gap: 16px;
  padding: 8px 0px;
`;

const InfoWrapper = styled(FlexView)`
  width: 320px;
  gap: ${({ gap }) => gap || '16'}px;
`;

const NameCardWrapper = styled(Row)`
  gap: 16px;
  margin-top: ${({ marginTop }) => marginTop};
`;

const NameCard = styled(Text)`
  min-width: 152px;
  line-height: 20px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  width: auto;
  font-weight: 700;
`;

const Button = styled(WCButton)`
  width: 100%;
`;

const InputWrapper = styled(Row)`
  width: 100%;
  height: 44px;

  div {
    width: 100%;
  }
`;

const getTransferRequestData = data => {
  const { studentName, teacherName, grade, region, classBalance, tenureEndDate } =
    data || {};

  return [
    {
      title: 'Student',
      value: studentName,
    },
    {
      title: 'Teacher',
      value: teacherName,
    },
    {
      title: 'Grade',
      value: grade,
    },
    {
      title: 'Region',
      value: region,
    },
    {
      title: 'Class Balance',
      value: classBalance,
    },
    {
      title: 'Tenure end date',
      value: getDateOfRequest(tenureEndDate) || '-',
    },
  ];
};

export const slotsInitState = [
  {
    day: '',
    time: '',
    id: `student-transfer-slots-${1}`,
  },
];

const TeacherNotFoundDetails = ({ data }) => {
  const { assigningSingleStudent, studentId, reasonForTransfer, courseStream } =
    data || {};

  const [teacherToBeAssigned, setTeacherToBeAssigned] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [didAssign, setDidAssign] = useState(false);
  const [slotsList, setSlotsList] = useState(slotsInitState);

  const onStudentTransferCompletion = error => {
    if (error) {
      setErrorMessage(error);
    } else {
      setDidAssign(true);
      setErrorMessage('');

      if (typeof assigningSingleStudent === 'function') {
        assigningSingleStudent();
      }
    }
  };

  const { post: initiateStudentTransfer, isProcessing } = postStudentTransferStatus({
    onComplete: onStudentTransferCompletion,
  });

  const findAndAssignMathTeacher = () => {
    const finalData = getSingleStudentTransferPostData({
      transferReason: reasonForTransfer,
      slotsList,
      preferredTeacherData: {
        preferredTeacherId: teacherToBeAssigned,
      },
    });

    if (reasonForTransfer === 'PREFERRED_TEACHER') {
      finalData.course_stream = courseStream;
    }

    initiateStudentTransfer(finalData, studentId);
  };

  const updateSlotsList = useCallback(updateSlotData => {
    setSlotsList(prev =>
      prev.map(slot => {
        if (slot.id === updateSlotData.id) {
          return updateSlotData;
        }
        return slot;
      }),
    );
  }, []);

  const addMoreSlots = () => {
    setSlotsList(prev => [
      ...prev,
      {
        day: '',
        time: '',
        id: `student-transfer-slots-${prev.length + 1}`,
      },
    ]);
  };

  const message =
    typeof errorMessage !== 'string'
      ? errorMessage?.course_stream || errorMessage?.preferred_teacher_id
      : errorMessage;
  const transferRequestData = getTransferRequestData(data);

  return (
    <Root>
      <InfoWrapper>
        {transferRequestData.map((item, index) => {
          const { title, value } = item || {};

          return (
            <NameCardWrapper key={title} alignItems="center">
              <NameCard variant="eyebrow2" color={toRGBA(COLORS.COLA, 0.75)}>
                {title}
              </NameCard>
              <POCValue variant="cta1">{value || '-'}</POCValue>
            </NameCardWrapper>
          );
        })}
      </InfoWrapper>

      <InputWrapper>
        <TextInput
          onChange={setTeacherToBeAssigned}
          value={teacherToBeAssigned}
          width="100%"
          label="Enter teacher ID"
        />
      </InputWrapper>

      <InfoWrapper gap={8}>
        <Row alignItem="center" justifyContent="space-between">
          <NameCard variant="eyebrow2">Regular Slots (IST)</NameCard>
          <LinkText variant="body1Regular" onClick={() => setSlotsList(slotsInitState)}>
            Reset
          </LinkText>
        </Row>

        {slotsList.map(slotData => {
          return (
            <StudentTransferSlotsList
              key={slotData.id}
              {...slotData}
              updateSlotsList={updateSlotsList}
              slotsList={slotsList}
            />
          );
        })}

        <LinkText variant="body1Regular" onClick={addMoreSlots}>
          Add more
        </LinkText>
      </InfoWrapper>

      <Button
        isBusy={isProcessing}
        onClick={findAndAssignMathTeacher}
        label="Assign teacher"
        isDisabled={didAssign || !teacherToBeAssigned}
      />

      {message && (
        <Text color={COLORS.RED_SHADE} variant="eyebrow2">
          {message}
        </Text>
      )}
    </Root>
  );
};

export default TeacherNotFoundDetails;
